import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  DeleteCredentialsRequestBody,
  GetCredentialsRequestQuery,
  GeneratePendingSshKeyResponse,
  GetUserCredentialsResponse,
  TestSSHConnectionRequestBody,
  TestSSHConnectionResponseBody,
  UpsertCredentialsRequestBody,
  UpsertCredentialsResponse,
} from "common";
import { useImpersonationUser } from "@via/hooks/useImpersonationUser.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";

const fetchSavedCredentials = async (
  params: GetCredentialsRequestQuery
): Promise<GetUserCredentialsResponse> => {
  try {
    const res = await axios.get("/api/user/v1/credentials", { params });
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetSavedCredentials = (
  params: GetCredentialsRequestQuery
): UseQueryResult<GetUserCredentialsResponse, Error> => {
  const impersonationId = useImpersonationUser();

  return useQuery<GetUserCredentialsResponse, Error>({
    queryKey: ["/api/user/v1/credentials", params, impersonationId],
    queryFn: () =>
      fetchSavedCredentials({
        ...params,
        ...(impersonationId && { userId: impersonationId }),
      }),
    retry: true,
  });
};

export const fetchGeneratedSSHKeys =
  async (): Promise<GeneratePendingSshKeyResponse> => {
    try {
      const res = await axios.post("/api/user/v1/credentials/generate-ssh");
      return res.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

/** Best effort: a pending key also expires on the server after 15 minutes. */
export const cancelGeneratedSSHKey = async (
  pendingKeyId: string
): Promise<void> => {
  try {
    await axios.delete(
      `/api/user/v1/credentials/generate-ssh/${encodeURIComponent(pendingKeyId)}`
    );
  } catch (error) {
    console.error(error);
  }
};

export const useCreateRunEnvironment = (): any => {
  const impersonationId = useImpersonationUser();

  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: UpsertCredentialsRequestBody) => {
      const res = await axios.post("/api/user/v1/credentials", {
        ...payload,
        ...(impersonationId && { ownerId: impersonationId }),
      });

      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "run-environment-successfully-saved",
        message: "Run Environments are saved",
        details: "Run Environments are successfully saved",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/user/v1/credentials"] });
    },
    onError: error => {
      setNotification({
        id: "run-environment-saving-failed",
        message: "Connection Failed",
        details: "Run Environments could not be saved",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteRunEnvironment = (): any => {
  const impersonationId = useImpersonationUser();
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (id: number) => {
      const payload: DeleteCredentialsRequestBody = {
        profileId: id,
        ...(impersonationId && { ownerId: impersonationId }),
      };

      const res = await axios.delete("/api/user/v1/credentials", {
        data: payload,
      });

      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "run-environment-successfully-deleted",
        message: "Run Environment is deleted",
        details: "Run Environment is successfully deleted",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/user/v1/credentials"] });
    },
    onError: error => {
      setNotification({
        id: "run-environment-deletion-failed",
        message: "Deletion Failed",
        details: "Run Environments could not be deleted",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUploadGoogleCredentialsFile = (): any => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        "/api/user/v1/credentials/import",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          params: {
            type: 2,
          },
        }
      );

      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "google-credentials-file-uploaded",
        message: "File Uploaded",
        details: "File is successfully uploaded",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "google-credentials-file-upload-failed",
        message: "File Upload Failed",
        details: "File could not be uploaded",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateCredential = (): UseMutationResult<
  UpsertCredentialsResponse,
  ErrorNotification,
  UpsertCredentialsRequestBody
> => {
  const impersonationId = useImpersonationUser();

  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: UpsertCredentialsRequestBody) => {
      const res = await axios.post("/api/user/v1/credentials", {
        ...payload,
        ...(impersonationId && { ownerId: impersonationId }),
      });

      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "credentials-successfully-saved",
        message: "Credentials are saved",
        details: "Credentials are successfully saved",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/user/v1/credentials"] });
    },
    onError: error => {
      setNotification({
        id: "credentials-saving-failed",
        message: "Connection Failed",
        details: "Credentials could not be saved",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteCredential = (): any => {
  const impersonationId = useImpersonationUser();
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: DeleteCredentialsRequestBody) => {
      const res = await axios.delete("/api/user/v1/credentials", {
        data: {
          ...payload,
          ...(impersonationId && { ownerId: impersonationId }),
        },
      });

      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "credential-successfully-deleted",
        message: "Credential is deleted",
        details: "Credential is successfully deleted",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/user/v1/credentials"] });
    },
    onError: error => {
      setNotification({
        id: "credential-deletion-failed",
        message: "Deletion Failed",
        details: "Credential could not be deleted",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useTestSSHConnection = (): any => {
  const impersonationId = useImpersonationUser();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: TestSSHConnectionRequestBody) => {
      const res = await axios.post(
        "/api/user/v1/credentials/test-ssh-connection",
        {
          ...payload,
          ...(impersonationId && { userId: impersonationId }),
        }
      );

      return res.data;
    },
    onSuccess: (response: TestSSHConnectionResponseBody): void => {
      if (response.success) {
        setNotification({
          id: "ssh-connection-successful",
          message: "SSH Connection is successful",
          details: "SSH Connection is successfully established",
          variant: NOTIFICATION.SUCCESS,
        });
      } else {
        setNotification({
          id: "ssh-connection-failed",
          message: "SSH Connection Failed",
          details: response.error,
          variant: NOTIFICATION.ERROR,
        });
      }
    },
    onError: error => {
      setNotification({
        id: "ssh-connection-failed",
        message: "SSH Connection Failed",
        details: "SSH Connection could not be established",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export { useCheckRunEnvironmentDiskSpace } from "@via/api/run-environment-disk-space.api.tsx";
